import React, { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Profile } from "./types";

interface UserTableProps {
  profiles: Profile[];
  loading: boolean;
  onEditUser: (profile: Profile) => void;
  onDeleteUser: (id: string) => void;
  onLoginToggle: (userId: string, enabled: boolean) => void;
  getSupervisorName: (supervisorId: string | null) => string;
}

const LoadingSkeleton = React.memo(() => (
  <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-4 h-[calc(100vh-320px)]">
    <div className="space-y-2">
      <Skeleton className="h-8 w-full mb-4" />
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={index} className="h-6 w-full" />
      ))}
    </div>
  </div>
));

LoadingSkeleton.displayName = "LoadingSkeleton";

type SortKey =
  | "full_name"
  | "email"
  | "company_name"
  | "role"
  | "supervisor"
  | "is_admin"
  | "module_access"
  | "enable_login"
  | "last_login";

type SortDir = "asc" | "desc";

const moduleAccessString = (p: Profile) =>
  [
    p.access_administration && "Admin",
    p.access_hr && "HR",
    p.access_sales && "Sales",
    p.access_marketing && "Marketing",
    p.access_finance && "Finance",
    p.access_projects && "Projects",
    p.access_legal && "Legal",
    p.access_performance && "Performance",
  ]
    .filter(Boolean)
    .join(",");

export const UserTable = React.memo(({
  profiles,
  loading,
  onEditUser,
  onDeleteUser,
  onLoginToggle,
  getSupervisorName
}: UserTableProps) => {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedProfiles = useMemo(() => {
    if (!sortKey) return profiles;
    const getVal = (p: Profile): string | number => {
      switch (sortKey) {
        case "full_name": return (p.full_name || "").toLowerCase();
        case "email": return (p.email || "").toLowerCase();
        case "company_name": return (p.company_name || "").toLowerCase();
        case "role": return (p.role || "").toLowerCase();
        case "supervisor": return getSupervisorName(p.supervisor_id).toLowerCase();
        case "is_admin": return p.is_admin ? "yes" : "no";
        case "module_access": return moduleAccessString(p).toLowerCase();
        case "enable_login": return p.enable_login ? "enable" : "disable";
        case "last_login": return p.last_login ? new Date(p.last_login).getTime() : 0;
        default: return "";
      }
    };
    const copy = [...profiles];
    copy.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [profiles, sortKey, sortDir, getSupervisorName]);

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <ArrowUpDown size={9} className="inline ml-1 opacity-50" />;
    return sortDir === "asc"
      ? <ArrowUp size={9} className="inline ml-1" />
      : <ArrowDown size={9} className="inline ml-1" />;
  };

  const sortableHeadClass = "text-primary text-left text-[9px] px-2 cursor-pointer select-none hover:text-primary/80";

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-4 h-[calc(100vh-320px)] flex flex-col">
      <Table>
        <TableHeader className="sticky top-0 z-10">
          <TableRow className="hover:bg-transparent bg-primary/20">
            <TableHead className="text-primary text-left text-[9px] w-[30px] px-2">SN</TableHead>
            <TableHead className={`${sortableHeadClass} w-[115px]`} onClick={() => handleSort("full_name")}>Full Name<SortIcon k="full_name" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[150px]`} onClick={() => handleSort("email")}>Email<SortIcon k="email" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[95px]`} onClick={() => handleSort("company_name")}>Company<SortIcon k="company_name" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[75px]`} onClick={() => handleSort("role")}>Role<SortIcon k="role" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[100px]`} onClick={() => handleSort("supervisor")}>Supervisor<SortIcon k="supervisor" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[55px]`} onClick={() => handleSort("is_admin")}>Is Admin<SortIcon k="is_admin" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[180px]`} onClick={() => handleSort("module_access")}>Module Access<SortIcon k="module_access" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[120px]`} onClick={() => handleSort("enable_login")}>Enable Login<SortIcon k="enable_login" /></TableHead>
            <TableHead className={`${sortableHeadClass} w-[140px]`} onClick={() => handleSort("last_login")}>Last Login<SortIcon k="last_login" /></TableHead>
            <TableHead className="text-primary text-left text-[9px] w-[60px] px-2">Actions</TableHead>
          </TableRow>
        </TableHeader>
      </Table>
      
      <ScrollArea className="flex-1 min-h-0">
        <Table>
          <TableBody>
            {sortedProfiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-primary-light text-[9px] py-10">
                  No users found. Add a new user to get started.
                </TableCell>
              </TableRow>
            ) : (
              sortedProfiles.map((profile, index) => (
                <TableRow key={profile.id} className="hover:bg-primary/10">
                  <TableCell className="font-medium text-white text-[9px] w-[30px] px-2 text-left">
                    {index + 1}
                  </TableCell>
                  <TableCell className="font-medium text-white text-[9px] w-[120px] px-2 text-left">
                    <div className="flex flex-col gap-1">
                      <span>{profile.full_name || "—"}</span>
                      {profile.is_unit_head && (
                        <Badge variant="destructive" className="w-fit text-[8px] px-1 py-0.5">
                          Unit Head
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-white text-[9px] w-[160px] px-2 text-left">
                    {profile.email}
                  </TableCell>
                  <TableCell className="text-white text-[9px] w-[100px] px-2 text-left">
                    {profile.company_name || "—"}
                  </TableCell>
                  <TableCell className="text-white text-[9px] w-[80px] px-2 text-left">
                    {profile.role || "—"}
                  </TableCell>
                  <TableCell className="text-white text-[9px] w-[100px] px-2 text-left">
                    {getSupervisorName(profile.supervisor_id)}
                  </TableCell>
                  <TableCell className="text-left w-[60px] px-2">
                    {profile.is_admin ? (
                      <div className="w-fit h-fit bg-primary/20 text-primary px-1 py-0.5 rounded-full text-[9px]">
                        Yes
                      </div>
                    ) : (
                      <div className="w-fit h-fit bg-gray-800 text-gray-400 px-1 py-0.5 rounded-full text-[9px]">
                        No
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="w-[200px] px-2 text-left">
                    <div className="flex flex-wrap gap-1">
                      {profile.access_administration && (
                        <div className="bg-indigo-900/30 text-indigo-300 px-1 py-0.5 rounded text-[9px]">
                          Admin
                        </div>
                      )}
                      {profile.access_hr && (
                        <div className="bg-green-900/30 text-green-300 px-1 py-0.5 rounded text-[9px]">
                          HR
                        </div>
                      )}
                      {profile.access_sales && (
                        <div className="bg-blue-900/30 text-blue-300 px-1 py-0.5 rounded text-[9px]">
                          Sales
                        </div>
                      )}
                      {profile.access_marketing && (
                        <div className="bg-purple-900/30 text-purple-300 px-1 py-0.5 rounded text-[9px]">
                          Marketing
                        </div>
                      )}
                      {profile.access_finance && (
                        <div className="bg-amber-900/30 text-amber-300 px-1 py-0.5 rounded text-[9px]">
                          Finance
                        </div>
                      )}
                      {profile.access_projects && (
                        <div className="bg-emerald-900/30 text-emerald-300 px-1 py-0.5 rounded text-[9px]">
                          Projects
                        </div>
                      )}
                      {profile.access_legal && (
                        <div className="bg-red-900/30 text-red-300 px-1 py-0.5 rounded text-[9px]">
                          Legal
                        </div>
                      )}
                      {profile.access_performance && (
                        <div className="bg-cyan-900/30 text-cyan-300 px-1 py-0.5 rounded text-[9px]">
                          Performance
                        </div>
                      )}
                      {!profile.access_administration &&
                        !profile.access_hr &&
                        !profile.access_sales &&
                        !profile.access_marketing &&
                        !profile.access_finance &&
                        !profile.access_projects &&
                        !profile.access_legal &&
                        !profile.access_performance && (
                          <div className="bg-gray-800 text-gray-400 px-1 py-0.5 rounded text-[9px]">
                            None
                          </div>
                        )}
                    </div>
                  </TableCell>
                  
                  <TableCell className="text-left w-[100px] px-2">
                    <RadioGroup
                      defaultValue={profile.enable_login ? "enabled" : "disabled"}
                      onValueChange={(value) => onLoginToggle(profile.id, value === "enabled")}
                      className="flex justify-start gap-2"
                    >
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="enabled" id={`enable-${profile.id}`} />
                        <Label htmlFor={`enable-${profile.id}`} className="text-[9px] text-white">Enable</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="disabled" id={`disable-${profile.id}`} />
                        <Label htmlFor={`disable-${profile.id}`} className="text-[9px] text-white">Disable</Label>
                      </div>
                    </RadioGroup>
                  </TableCell>

                  <TableCell className="text-white text-[9px] w-[140px] px-2 text-left">
                    {profile.last_login ? new Date(profile.last_login).toLocaleString() : "Never"}
                  </TableCell>

                  <TableCell className="text-left w-[60px] px-2">
                    <div className="flex justify-start gap-1">
                      {!profile.is_admin && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 bg-transparent hover:bg-primary/10 text-primary"
                            onClick={() => onEditUser(profile)}
                          >
                            <Edit size={12} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 bg-transparent hover:bg-red-500/10 text-red-500"
                            onClick={() => onDeleteUser(profile.id)}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
});

UserTable.displayName = "UserTable";
